import { PhotoSwipeLightbox } from 'vendor';

/**
 * Bundle PDP: per-child variant selection, gallery image updates, multi-item add to cart.
 */
class BundleProduct extends HTMLElement {
  #products = [];
  #config = {};
  #selected = new Map();

  connectedCallback() {
    const productsEl = this.querySelector('[data-bundle-products]');
    const configEl = this.querySelector('[data-bundle-config]');
    if (!productsEl || !configEl) return;

    try {
      this.#products = JSON.parse(productsEl.textContent);
      this.#config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[bundle-product] Failed to parse config', error);
      return;
    }

    this.#products.forEach((product) => {
      const initial = product.variants.find((v) => v.available) || product.variants[0];
      this.#selected.set(product.id, {
        options: initial ? [...initial.options] : [],
        variant: initial || null
      });
    });

    this.addEventListener('change', this.#onOptionChange);
    this.querySelector('[data-bundle-atc]')?.addEventListener('click', this.#onAddToCart);
    document.addEventListener('click', this.#onStickyAddToCart);

    this.#syncAll();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onOptionChange);
    this.querySelector('[data-bundle-atc]')?.removeEventListener('click', this.#onAddToCart);
    document.removeEventListener('click', this.#onStickyAddToCart);
  }

  #onOptionChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const item = input.closest('[data-bundle-item]');
    if (!item) return;

    const productId = Number(item.dataset.productId);
    const product = this.#products.find((p) => p.id === productId);
    if (!product) return;

    const fieldset = input.closest('[data-option-index]');
    const optionIndex = Number(fieldset?.dataset.optionIndex ?? 0);
    const state = this.#selected.get(productId);
    if (!state) return;

    const label = this.#getInputOptionName(
      input,
      input.closest('label') ||
        item.querySelector(`label[for="${CSS.escape(input.id)}"]`) ||
        input.nextElementSibling
    );
    if (!label) return;

    state.options[optionIndex] = label;
    state.variant = this.#findVariant(product, state.options);

    if (!state.variant?.available) {
      const fallback = this.#findNearestAvailable(product, state.options, optionIndex);
      if (fallback) {
        state.options = [...fallback.options];
        state.variant = fallback;
        this.#syncRadioSelection(item, product, state);
      }
    }

    this.#selected.set(productId, state);

    const selectedLabel = fieldset?.querySelector('[data-option-selected-label]');
    if (selectedLabel) selectedLabel.textContent = state.options[optionIndex] || label;

    this.#syncItem(item, product, state);
    this.#updateGallery(product, state.variant, { scroll: true });
    this.#updateAtcState();
  };

  #findNearestAvailable(product, selectedOptions, lockedIndex) {
    return (
      product.variants.find((variant) => {
        if (!variant.available) return false;
        return variant.options[lockedIndex] === selectedOptions[lockedIndex];
      }) || product.variants.find((variant) => variant.available) || null
    );
  }

  #syncRadioSelection(item, product, state) {
    item.querySelectorAll('[data-option-index]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex);
      const wanted = state.options[optionIndex];
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const name = this.#getInputOptionName(
          input,
          input.closest('label') ||
            fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`) ||
            input.nextElementSibling
        );
        input.checked = name === wanted;
      });
      const selectedLabel = fieldset.querySelector('[data-option-selected-label]');
      if (selectedLabel && wanted) selectedLabel.textContent = wanted;
    });
  }

  #findVariant(product, selectedOptions) {
    return product.variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    ) || null;
  }

  #getVariantImages(product, variant) {
    if (!product?.media?.length) return [];

    if (variant?.featured_media_id) {
      const start = product.media.findIndex((m) => m.id === variant.featured_media_id);
      if (start >= 0) return product.media.slice(start, start + 2);
    }

    if (variant?.sku) {
      const matched = product.media.filter((m) => m.filename?.includes(variant.sku));
      if (matched.length) return matched.slice(0, 2);
    }

    return product.media.slice(0, 2);
  }

  #updateGallery(product, variant, { scroll = false } = {}) {
    const root = this.closest('.quick-buy') || this.closest('[data-product-id]') || document;
    const gallery = root.querySelector('[data-bundle-gallery]');
    if (!gallery) return;

    const images = this.#getVariantImages(product, variant);
    const objectFit = gallery.dataset.layout === 'carousel' ? 'contain' : 'cover';
    gallery.querySelectorAll(`[data-bundle-product-id="${product.id}"][data-bundle-image-index]`).forEach((cell) => {
      const index = Number(cell.dataset.bundleImageIndex);
      const image = images[index];
      if (!image?.src) return;

      const imageWrap = cell.querySelector('[data-bundle-image]');
      if (!imageWrap) return;

      const img = document.createElement('img');
      img.src = image.src;
      img.alt = image.alt || product.title || '';
      if (image.width) img.width = image.width;
      if (image.height) img.height = image.height;
      img.loading = 'lazy';
      img.style.cssText = `width:100%;height:100%;object-fit:${objectFit};display:block;`;
      imageWrap.replaceChildren(img);
    });

    if (!scroll || gallery.dataset.layout !== 'carousel') return;

    const firstCell = gallery.querySelector(`[data-bundle-product-id="${product.id}"][data-bundle-image-index="0"]`);
    const carousel = gallery.querySelector('scroll-carousel');
    const slideIndex = Number(firstCell?.dataset.imageIndex);
    if (firstCell && carousel && typeof carousel.select === 'function' && Number.isInteger(slideIndex)) {
      carousel.select(slideIndex);
    }
  }

  #syncItem(item, product, state) {
    const variantInput = item.querySelector('[data-bundle-variant-id]');
    if (variantInput) {
      variantInput.value = state.variant?.id || '';
    }

    const sizeLabel = item.querySelector('[data-bundle-size-label]');
    if (sizeLabel && state.variant) {
      // Keep metafield text if present; otherwise show variant size option
      if (!product.sizeMetafield) {
        const sizeIndex = product.options.findIndex((o) => o.toLowerCase() === 'size');
        if (sizeIndex >= 0) sizeLabel.textContent = state.options[sizeIndex] || '';
      }
    }

    this.#updateOptionAvailability(item, product, state);
  }

  #updateOptionAvailability(item, product, state) {
    item.querySelectorAll('[data-option-index]').forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.optionIndex);
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const labelEl =
          input.closest('label') ||
          fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`) ||
          input.nextElementSibling;
        const valueName = this.#getInputOptionName(input, labelEl);
        if (!valueName) return;

        const trial = [...state.options];
        trial[optionIndex] = valueName;
        const exactMatch = this.#findVariant(product, trial);
        const existsForOption = product.variants.some(
          (variant) => variant.options[optionIndex] === valueName && variant.available
        );
        const unavailable = !exactMatch || !exactMatch.available;
        const isThumbnail = labelEl?.classList.contains('thumbnail-swatch');

        // Never disable radios — theme CSS hides `:disabled + .thumbnail-swatch`.
        // Color thumbnails stay visible like Offset PDP; unavailable lens/size hide via is-disabled.
        input.disabled = false;

        if (isThumbnail) {
          labelEl?.classList.toggle('is-disabled', !existsForOption);
          labelEl?.classList.remove('line-through');
        } else {
          labelEl?.classList.toggle('is-disabled', unavailable);
          labelEl?.classList.toggle('line-through', unavailable && Boolean(exactMatch));
        }
      });
    });
  }

  #getInputOptionName(input, labelEl) {
    const aria = input?.getAttribute('aria-label')?.trim();
    if (aria) return aria;

    const blockText = labelEl?.querySelector('span:not(.block-swatch__color)')?.textContent?.trim();
    if (blockText) return blockText;

    return labelEl?.getAttribute('title')?.trim() || input?.getAttribute('title')?.trim() || null;
  }

  #syncAll() {
    this.#products.forEach((product) => {
      const item = this.querySelector(`[data-product-id="${product.id}"]`);
      const state = this.#selected.get(product.id);
      if (!item || !state) return;
      this.#syncItem(item, product, state);
      this.#updateGallery(product, state.variant);
    });
    this.#updateAtcState();
  }

  #allVariantsAvailable() {
    return this.#products.every((product) => {
      const state = this.#selected.get(product.id);
      return state?.variant?.available;
    });
  }

  #updateAtcState() {
    const available = this.#allVariantsAvailable();
    const mainLabelText = available
      ? `${this.#config.buttonLabelTemplate} - ${this.#config.parentPriceFormatted}`
      : this.#config.soldOutLabel || 'Sold out';
    const stickyLabelText = available
      ? this.#config.stickyAtcLabel || 'Add to cart'
      : this.#config.soldOutLabel || 'Sold out';

    const button = this.querySelector('[data-bundle-atc]');
    const label = this.querySelector('[data-bundle-atc-label]');
    if (button) button.disabled = !available;
    if (label) label.textContent = mainLabelText;

    const mainButtonContent = button?.querySelector('button-content');
    if (mainButtonContent) {
      if (mainButtonContent.hasAttribute('rotated-text')) {
        mainButtonContent.setAttribute('rotated-text', mainLabelText);
      }
      if (mainButtonContent.hasAttribute('up-text')) {
        mainButtonContent.setAttribute('up-text', mainLabelText);
      }
    }

    document.querySelectorAll('[data-bundle-sticky-atc]').forEach((stickyButton) => {
      stickyButton.disabled = !available;
      const stickyLabel = stickyButton.querySelector('[data-bundle-sticky-atc-label]');
      if (stickyLabel) stickyLabel.textContent = stickyLabelText;

      const buttonContent = stickyButton.querySelector('button-content');
      if (!buttonContent) return;
      if (buttonContent.hasAttribute('rotated-text')) {
        buttonContent.setAttribute('rotated-text', stickyLabelText);
      }
      if (buttonContent.hasAttribute('up-text')) {
        buttonContent.setAttribute('up-text', stickyLabelText);
      }
    });
  }

  #onStickyAddToCart = (event) => {
    const stickyButton = event.target.closest('[data-bundle-sticky-atc]');
    if (!stickyButton || stickyButton.disabled) return;

    event.preventDefault();
    this.querySelector('[data-bundle-atc]')?.click();
  };

  #onAddToCart = async (event) => {
    const button = event.currentTarget;
    if (button.getAttribute('aria-busy') === 'true' || button.disabled) return;
    if (!this.#allVariantsAvailable()) return;

    const errorEl = this.querySelector('[data-bundle-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const instanceId = `${this.#config.parentId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const childProperties = {
      _bundle: this.#config.parentHandle,
      _bundle_id: instanceId,
      _bundle_title: this.#config.parentTitle,
      _is_part_of_bundle: 'true'
    };

    const items = [
      ...this.#products.map((product) => {
        const state = this.#selected.get(product.id);
        return {
          id: state.variant.id,
          quantity: 1,
          properties: { ...childProperties }
        };
      }),
      {
        id: this.#config.parentVariantId,
        quantity: 1,
        properties: {
          _bundle_instance: instanceId
        }
      }
    ];

    button.setAttribute('aria-busy', 'true');
    document.querySelectorAll('[data-bundle-sticky-atc]').forEach((stickyButton) => {
      stickyButton.setAttribute('aria-busy', 'true');
    });
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: [...sectionsToBundle].join(',')
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'Unable to add bundle to cart');
      }

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${window.Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent
          }
        })
      );

      if (window.themeVariables?.settings?.cartType === 'message') {
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: { message: `${this.#config.parentTitle} added to cart` }
          })
        );
      }
    } catch (error) {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = error.message || 'Unable to add bundle to cart';
      }
    } finally {
      button.removeAttribute('aria-busy');
      document.querySelectorAll('[data-bundle-sticky-atc]').forEach((stickyButton) => {
        stickyButton.removeAttribute('aria-busy');
      });
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  };
}

if (!customElements.get('bundle-product')) {
  customElements.define('bundle-product', BundleProduct);
}

class BundleGallery extends HTMLElement {
  #photoSwipeInstance = null;
  #carousel = null;
  #snapTimeout = null;
  #isSnapping = false;

  constructor() {
    super();
    this.addEventListener('lightbox:open', (event) => this.openLightBox(event?.detail?.index));
  }

  connectedCallback() {
    this.#carousel = this.querySelector('scroll-carousel');
    if (this.#carousel) {
      this.#carousel.addEventListener('scrollend', this.#onScrollEnd);
      this.#carousel.addEventListener('touchend', this.#onTouchEnd, { passive: true });
      this.#carousel.addEventListener('carousel:settle', this.#onSettle);
    }

    if (!this.hasAttribute('allow-zoom')) return;
    this.addEventListener('click', this.#onGalleryClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onGalleryClick);
    if (this.#carousel) {
      this.#carousel.removeEventListener('scrollend', this.#onScrollEnd);
      this.#carousel.removeEventListener('touchend', this.#onTouchEnd);
      this.#carousel.removeEventListener('carousel:settle', this.#onSettle);
    }
    clearTimeout(this.#snapTimeout);
  }

  #onTouchEnd = () => {
    // Fallback when scrollend is missing or delayed after momentum scrolling.
    clearTimeout(this.#snapTimeout);
    this.#snapTimeout = setTimeout(() => this.#snapToNearestSlide(), 120);
  };

  #onScrollEnd = () => {
    clearTimeout(this.#snapTimeout);
    this.#snapToNearestSlide();
  };

  #onSettle = () => {
    this.#isSnapping = false;
  };

  #snapToNearestSlide = () => {
    if (!this.#carousel || typeof this.#carousel.select !== 'function') return;
    if (this.dataset.layout !== 'carousel' && window.matchMedia('(min-width: 1000px)').matches) return;
    if (this.#isSnapping) return;

    const index = this.#carousel.selectedIndex;
    if (!Number.isInteger(index) || index < 0) return;

    this.#isSnapping = true;
    this.#carousel.select(index);

    // select() can no-op when already aligned and skip carousel:settle
    clearTimeout(this.#snapTimeout);
    this.#snapTimeout = setTimeout(() => {
      this.#isSnapping = false;
    }, 450);
  };

  get lightBox() {
    if (this.#photoSwipeInstance) return this.#photoSwipeInstance;

    this.#photoSwipeInstance = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      mainClass: `color-scheme--${this.getAttribute('zoom-color-scheme-id')}`,
      maxZoomLevel: parseInt(this.getAttribute('allow-zoom'), 10) || 3,
      closeTitle: window.themeVariables?.strings?.closeGallery,
      zoomTitle: window.themeVariables?.strings?.zoomGallery,
      errorMsg: window.themeVariables?.strings?.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    });

    this.#photoSwipeInstance.on('uiRegister', () => {
      this.#photoSwipeInstance.pswp.ui.registerElement({
        name: 'close-button circle-button circle-button--xl',
        className: '',
        ariaLabel: window.themeVariables?.strings?.closeGallery,
        order: 2,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="14" class="icon" viewBox="0 0 16 16">
            <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
          </svg>
        `,
        onClick: () => this.#photoSwipeInstance.pswp.close()
      });

      if (this.#photoSwipeInstance.pswp.options.dataSource.length > 1) {
        this.#photoSwipeInstance.pswp.ui.registerElement({
          name: 'previous-button circle-button circle-button--xl',
          className: '',
          ariaLabel: window.themeVariables?.strings?.previous,
          order: 1,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" width="12" class="icon icon--direction-aware" viewBox="0 0 36 36">
              <path fill="none" stroke="currentColor" stroke-width="2" d="M35 18H3M19.5 34.5 3 18 19.5 1.5"/>
            </svg>
          `,
          onClick: () => this.#photoSwipeInstance.pswp.prev()
        });

        this.#photoSwipeInstance.pswp.ui.registerElement({
          name: 'next-button circle-button circle-button--xl',
          className: '',
          ariaLabel: window.themeVariables?.strings?.next,
          order: 3,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" width="12" class="icon icon--direction-aware" viewBox="0 0 36 36">
              <path fill="none" stroke="currentColor" stroke-width="2" d="M1 18h32M16.5 1.5 33 18 16.5 34.5"/>
            </svg>
          `,
          onClick: () => this.#photoSwipeInstance.pswp.next()
        });
      }
    });

    this.#photoSwipeInstance.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);
    this.#photoSwipeInstance.init();
    return this.#photoSwipeInstance;
  }

  #onGalleryClick = (event) => {
    if (event.target.closest('button, a[href], open-lightbox-button')) return;

    if (this.dataset.layout === 'carousel') {
      if (!window.matchMedia('screen and (pointer: fine)').matches) return;
      const carousel = this.#carousel;
      if (!carousel || typeof carousel.next !== 'function') return;
      if (!event.target.closest('[data-bundle-media][data-media-type="image"]')) return;

      const rect = carousel.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      offsetX > carousel.clientWidth / 2 ? carousel.next() : carousel.previous();
      return;
    }

    const media = event.target.closest('[data-bundle-media][data-media-type="image"]');
    if (!media) return;

    const index = media.hasAttribute('data-image-index')
      ? parseInt(media.getAttribute('data-image-index'), 10)
      : null;
    this.openLightBox(Number.isFinite(index) ? index : null);
  };

  openLightBox(index) {
    const mediaCells = Array.from(this.querySelectorAll('[data-bundle-media][data-media-type="image"]'));
    const images = mediaCells
      .map((cell) => cell.querySelector('picture img, [data-bundle-image] img, :scope > img'))
      .filter(Boolean);

    const dataSource = images.map((image) => ({
      thumbnailElement: image,
      src: image.src,
      srcset: image.srcset,
      msrc: image.currentSrc || image.src,
      width: parseInt(image.getAttribute('width'), 10) || image.naturalWidth || 1200,
      height: parseInt(image.getAttribute('height'), 10) || image.naturalHeight || 1200,
      alt: image.alt,
      thumbCropped: true
    }));

    if (!dataSource.length) return;

    const resolvedIndex = Number.isFinite(index) ? index : 0;
    this.lightBox.loadAndOpen(Math.max(0, Math.min(resolvedIndex, dataSource.length - 1)), dataSource);
  }
}

if (!customElements.get('bundle-gallery')) {
  customElements.define('bundle-gallery', BundleGallery);
}
